import React from 'react';
import { Link } from 'react-router-dom';
import '../../../global.css';
import Navbar from '../../../components/Navbar';
import SiteFooter from '../../../components/SiteFooter';
import NeuralCanvas from '../../../components/NeuralCanvas';
import { useSEO } from '../../../hooks/useSEO';
import safetyHero from '../../../assets/images/fin.jpeg'; // Import your local image

export default function Fintech() {
  useSEO({
    title: 'Finance & Inclusion AI for Africa',
    description: 'Bringing financial access to everyone — credit scoring for mobile money users, instant fraud protection, and multilingual financial guidance for cooperatives and savings groups across Africa.',
    path: '/solutions/fintech',
  });
  return (
    <>
      <NeuralCanvas />
      <div className="neural-page">
      <Navbar />

      <div style={{ borderBottom: '1px solid var(--line)', padding: '80px 0 72px' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--pad-x)' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 20 }}>SOLUTION · FINANCE & INCLUSION</div>
          <div className="sol-hero-grid">
            <div>
              <h1 style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 400, letterSpacing: '-0.04em', lineHeight: 1.05, marginBottom: 20 }}>
                Financial access<br /><span style={{ color: 'var(--fg-mid)', fontWeight: 300 }}>for everyone.</span>
              </h1>
              <p style={{ fontSize: 16, color: 'var(--fg-mid)', lineHeight: 1.7, maxWidth: 480, marginBottom: 32 }}>
                Most Africans don't have a formal credit history. Cybernate reads mobile money patterns
                to assess creditworthiness — so anyone with a phone can access loans, insurance, and savings,
                in their own language, over any channel they already use.
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                <Link to="/signup" className="btn btn-primary">Get started →</Link>
                <Link to="/developers" className="btn btn-ghost">Developer platform</Link>
              </div>
            </div>
            <div style={{ padding: '56px 0 0' }}>
            <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--pad-x)' }}>
              <div style={{
                width: '100%',
                height: 'clamp(220px, 42vw, 480px)',
                borderRadius: 'var(--r-lg)',
                overflow: 'hidden',
                border: '1px solid var(--line)',
                background: 'var(--bg-1)',
              }}>
                <img
                  src={safetyHero}
                  alt="Mobile money and financial access across Africa"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      <div style={{ borderBottom: '1px solid var(--line)', padding: '40px 0', background: 'var(--bg-1)' }}>
        <div className="sol-stats-grid" style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--pad-x)' }}>
          {[['$180B', 'Africa\'s AI economic opportunity (McKinsey)'], ['M-Pesa+', 'Mobile money integrations'], ['<1s', 'Fraud detection latency'], ['18', 'Languages for advisory']].map(([n, l], i) => (
            <div key={n} style={{ padding: '24px 32px', borderRight: i < 3 ? '1px solid var(--line)' : 'none' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 28, fontWeight: 500, letterSpacing: '-0.04em', color: 'var(--fg)', marginBottom: 6 }}>{n}</div>
              <div style={{ fontSize: 12, color: 'var(--fg-dim)' }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════
          PHOTO SECTION — uncomment when image is ready.
          Replace src with your Unsplash URL.
          Tip: append ?w=1400&q=85&fit=crop to any Unsplash URL.
          ═══════════════════════════════════════════════════ */}
      {/*
      <div style={{ padding: '56px 0 0' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--pad-x)' }}>
          <div style={{
            width: '100%',
            height: 'clamp(220px, 42vw, 480px)',
            borderRadius: 'var(--r-lg)',
            overflow: 'hidden',
            border: '1px solid var(--line)',
            background: 'var(--bg-1)',
          }}>
            <img
              src="YOUR_IMAGE_URL_HERE"
              alt="Mobile money and financial access across Africa"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
              loading="lazy"
            />
          </div>
        </div>
      </div>
      */}

      <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '72px var(--pad-x)' }}>
        <h2 style={{ fontSize: 32, fontWeight: 400, letterSpacing: '-0.03em', marginBottom: 48 }}>Financial AI built<br /><span style={{ color: 'var(--fg-mid)', fontWeight: 300 }}>for how Africa pays.</span></h2>
        <div className="sol-features-grid">
          {[
            { title: 'Alternative credit scoring', desc: 'Score borrowers using mobile money history, airtime purchases, and social data — not credit bureau reports that most Africans don\'t have.' },
            { title: 'Real-time fraud detection', desc: 'Detect anomalies in M-Pesa, Flutterwave, and Paystack transactions in under one second. Models trained on African transaction patterns.' },
            { title: 'Cooperative intelligence', desc: 'Analyze a savings cooperative\'s books and surface cashflow risks, member default signals, and lending opportunities in plain Swahili or Yoruba.' },
            { title: 'Document processing', desc: 'Extract, verify, and classify documents for KYC — passports, national IDs, utility bills from all 54 African nations.' },
            { title: 'Financial advisory chatbot', desc: 'A multilingual AI financial advisor for retail banking customers. Explains products in Pidgin, answers questions over WhatsApp or USSD.' },
            { title: 'Regulatory compliance', desc: 'Monitor transactions for AML and CFT compliance across ECOWAS, EAC, and SADC regulatory frameworks.' },
          ].map(u => (
            <div key={u.title} style={{ padding: '24px', background: 'var(--bg-1)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)' }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--fg)', marginBottom: 8 }}>{u.title}</div>
              <div style={{ fontSize: 13, color: 'var(--fg-dim)', lineHeight: 1.65 }}>{u.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--line)', padding: '64px 0', background: 'var(--bg-1)' }}>
        <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center', padding: '0 var(--pad-x)' }}>
          <h2 style={{ fontSize: 28, fontWeight: 400, letterSpacing: '-0.03em', marginBottom: 12 }}>Unlock financial access for Africa.</h2>
          <p style={{ fontSize: 14, color: 'var(--fg-mid)', marginBottom: 28 }}>Free to start. Works across M-Pesa, USSD, and WhatsApp. No credit card required.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Link to="/signup" className="btn btn-primary btn-lg">Get started →</Link>
            <Link to="/developers" className="btn btn-ghost btn-lg">Developer docs</Link>
          </div>
        </div>
      </div>

      <SiteFooter />
      </div>
    </>
  );
}
